/* eslint-disable react-hooks/rules-of-hooks */
import { useRequest } from 'ahooks';
import { Formik, FormikConfig, FormikProps } from 'formik';
import { ReactNode, useEffect, useState } from 'react';
import {
  createErpOrder,
  getErpOrder,
  getJstTraceChannel,
  getQyTraceChannel,
  reviewErpOrder,
} from './api';
import FormField from 'components/FormField';
import {
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  ModalProps,
  Pagination,
  Radio,
  Select,
} from 'antd';
import { useTranslate } from '@tolgee/react';
import SafeImage from 'components/SafeImage';
import { CopyOutlined, FormOutlined, LoadingOutlined } from '@ant-design/icons';
import copyText from 'utils/copyText';
import styled from '@emotion/styled';
import { productList } from 'services/api/conversation';
import Empty from 'components/Empty';
import useModal from 'hooks/useModal';
import StickyModal from 'components/StickyModal';
import { find } from 'lodash-es';
import findMostSimilar from 'utils/findMostSimilar';
import StatusButton from 'components/StatusButton';
import { extractAddressInfo } from 'services/api/conversation.convList';

export type FV = Partial<
  Pick<
    Todo.ErpOrder,
    | 'todoId'
    | 'itemId'
    | 'skuId'
    | 'skuName'
    | 'skuQuantity'
    | 'skuAmount'
    | 'currency'
    | 'erpTradeId'
    | 'tradeId'
    | 'receiverName'
    | 'country'
    | 'province'
    | 'city'
    | 'district'
    | 'postCode'
    | 'address'
    | 'erpType'
    | 'phone'
    | 'warehouseName'
    | 'warehouseCode'
    | 'traceChannel'
    | 'channelType'
    | 'erpId'
  >
>;

export const createInitialValues = (
  partial: Util.PartialRequired<
    FV,
    'todoId' | 'tradeId' | 'currency' | 'skuId' | 'skuName' | 'country' | 'erpType'
  >,
): FV => {
  return {
    skuQuantity: 1,
    skuAmount: '0',
    receiverName: '',
    province: '',
    city: '',
    district: '',
    postCode: '',
    address: '',
    phone: '',
    warehouseCode: undefined,
    warehouseName: undefined,
    traceChannel: undefined,
    channelType: undefined,
    ...partial,
  };
};

interface ErpOrderFormProps extends FormikConfig<FV> {
  disabled?: boolean;
  storeId: number;
  wms: ERP.Wm[];
  /** 平台名称，如 Shopee，用于匹配物流商、物流渠道 */
  platform?: string;
  skuInfo: Conversation.ProductSKUInfo[];
  children: (form: ReactNode, props: FormikProps<FV>) => ReactNode;
}

function ErpOrderForm({
  disabled,
  storeId,
  wms,
  platform,
  skuInfo,
  children,
  initialValues,
  onSubmit,
  ...otherProps
}: ErpOrderFormProps) {
  const { t } = useTranslate();

  const [newDelivery, setNewDelivery] = useState(false);

  useEffect(() => {
    const { receiverName, erpType } = initialValues;
    if (erpType === 'JST') {
      setNewDelivery(Boolean(receiverName));
    }
  }, [initialValues]);

  const [text, setText] = useState('');

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, helpers) => {
        if (!newDelivery && values.erpType === 'JST') {
          return onSubmit(
            {
              ...values,
              receiverName: '',
              phone: '',
              postCode: '',
              province: '',
              city: '',
              district: '',
              address: '',
            },
            helpers,
          );
        }
        return onSubmit(values, helpers);
      }}
      {...otherProps}
    >
      {(formProps) => {
        const { values, setValues } = formProps;

        const {
          skuId,
          currency,
          erpType,
          warehouseName,
          warehouseCode,
          traceChannel,
          skuQuantity,
        } = values;

        const qyTraceChannelState = useRequest(
          () =>
            getQyTraceChannel({
              storeId,
              warehouseName,
            }),
          {
            ready: erpType === 'QY' && Boolean(warehouseName),
            refreshDeps: [warehouseName],
            onSuccess: (data) => {
              if (data.every((_) => _.code !== traceChannel?.code)) {
                setValues({
                  ...values,
                  traceChannel: undefined,
                });
              }
            },
          },
        );
        const jstTraceChannelState = useRequest(() => getJstTraceChannel({ storeId }), {
          ready: erpType === 'JST',
          onSuccess: (data) => {
            if (
              data.every(({ channel, channelType }) => {
                return (
                  channelType !== values.channelType ||
                  channel.every(({ code }) => code !== traceChannel?.code)
                );
              })
            ) {
              setValues({
                ...values,
                channelType: undefined,
                traceChannel: undefined,
              });
            }
          },
        });
        const jstChannels = jstTraceChannelState.data?.find(
          ({ channelType }) => channelType === values.channelType,
        )?.channel;

        // 以发货仓名称为参照，匹配最相似的物流商、物流渠道（物流渠道依赖物流商，先定物流商再匹配渠道）
        const matchJstLogistics = (name: string): Pick<FV, 'channelType' | 'traceChannel'> => {
          const carrier = findMostSimilar(name, jstTraceChannelState.data, (_) => _.name, platform);
          if (!carrier) {
            return {};
          }
          return {
            channelType: carrier.channelType,
            traceChannel: findMostSimilar(name, carrier.channel, (_) => _.name, platform),
          };
        };

        const targetSku = skuInfo.find((_) => _.skuId === skuId);

        const wmList = targetSku?.erpStock?.reduce<
          (ERP.Wm & {
            warehouseName: string;
            warehouseInventory: number;
          })[]
        >((res, cur) => {
          const target = wms.find((_) => _.name === cur.warehouseName);
          if (target) {
            res.push({
              ...target,
              ...cur,
            });
          }
          return res;
        }, []);

        const wm = wmList?.find(
          (_) => (erpType === 'QY' ? _.name : `${_.warehouseId}`) === warehouseCode,
        );

        const form = (
          <Form layout="vertical" colon={false}>
            <Flex items={[3, 1, 2]}>
              <FormField<FV['skuId']>
                name="skuId"
                required
                label="SKU"
                validate={(value) =>
                  value
                    ? undefined
                    : t('common.validation.requiredSelection', {
                        fieldName: t(
                          'scene.chatWorkspace.taskTab.erpOrderForm.label.storeSkuShort',
                        ),
                      })
                }
              >
                {({ field: { value, onBlur } }) => {
                  return (
                    <Select
                      dropdownMatchSelectWidth={false}
                      showSearch
                      optionFilterProp="label"
                      disabled={disabled}
                      placeholder={t('common.validation.requiredSelection', {
                        fieldName: t(
                          'scene.chatWorkspace.taskTab.erpOrderForm.label.storeSkuShort',
                        ),
                      })}
                      value={value}
                      onBlur={onBlur}
                      onChange={(value) => {
                        const targetSku = skuInfo.find((_) => _.skuId === value);
                        if (targetSku) {
                          setValues({
                            ...values,
                            skuId: targetSku.skuId,
                            skuName: targetSku.sellerSku,
                            warehouseCode: undefined,
                            warehouseName: undefined,
                          });
                        }
                      }}
                      options={skuInfo.map(({ skuId, sku, sellerSku }) => ({
                        value: skuId,
                        label: `${sku} | ${sellerSku}`,
                      }))}
                    />
                  );
                }}
              </FormField>
              <FormField<FV['skuQuantity']>
                name="skuQuantity"
                required
                label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.pieceCount')}
                validate={(value) =>
                  value
                    ? undefined
                    : t('common.validation.requiredInput', {
                        fieldName: t('scene.chatWorkspace.taskTab.erpOrderForm.label.pieceCount'),
                      })
                }
              >
                {({ field: { value, onBlur } }) => {
                  return (
                    <InputNumber
                      disabled={disabled}
                      addonAfter={t('scene.chatWorkspace.taskTab.erpOrderForm.unit.piece')}
                      precision={0}
                      min={1}
                      value={value}
                      onChange={(value) =>
                        setValues({ ...values, skuQuantity: value ?? undefined })
                      }
                      onBlur={onBlur}
                    />
                  );
                }}
              </FormField>
              <FormField<FV['skuAmount']>
                name="skuAmount"
                required
                label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.amount')}
                validate={(value) =>
                  value
                    ? undefined
                    : t('common.validation.requiredInput', {
                        fieldName: t('scene.chatWorkspace.taskTab.erpOrderForm.label.amount'),
                      })
                }
              >
                {({ field: { value, onBlur } }) => {
                  return (
                    <InputNumber
                      disabled={disabled}
                      addonAfter={currency}
                      value={value ? Number(value) : undefined}
                      onChange={(value) =>
                        setValues({
                          ...values,
                          skuAmount: typeof value === 'number' ? value.toString() : '',
                        })
                      }
                      onBlur={onBlur}
                    />
                  );
                }}
              </FormField>
            </Flex>
            <FormField<FV['warehouseCode']>
              name="warehouseCode"
              required
              label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.shipFromWarehouse')}
              validate={(value) =>
                value
                  ? undefined
                  : t('common.validation.requiredSelection', {
                      fieldName: t(
                        'scene.chatWorkspace.taskTab.erpOrderForm.label.shipFromWarehouse',
                      ),
                    })
              }
            >
              {({ field: { value, onBlur } }) => {
                return (
                  <Flex items={[1, 1]}>
                    <Select
                      disabled={disabled}
                      placeholder={t('common.validation.requiredSelection', {
                        fieldName: t(
                          'scene.chatWorkspace.taskTab.erpOrderForm.label.shipFromWarehouse',
                        ),
                      })}
                      value={value}
                      onChange={(code) => {
                        const target = wmList?.find(
                          (_) => (erpType === 'QY' ? _.name : `${_.warehouseId}`) === code,
                        );
                        if (target) {
                          setValues({
                            ...values,
                            warehouseCode: code,
                            warehouseName: target.name,
                            ...matchJstLogistics(target.name),
                          });
                        }
                      }}
                      onBlur={onBlur}
                      options={wmList?.map(({ warehouseId, name }) => ({
                        value: erpType === 'QY' ? name : `${warehouseId}`,
                        label: name,
                      }))}
                    />
                    {wm ? (
                      <span>
                        {t('scene.chatWorkspace.taskTab.erpOrderForm.label.inventory')}：
                        {wm.warehouseInventory}
                      </span>
                    ) : (
                      <span />
                    )}
                  </Flex>
                );
              }}
            </FormField>
            <Flex items={[1, 1]}>
              <FormField<FV['traceChannel']>
                name="traceChannel"
                required={erpType === 'QY'}
                label={
                  erpType === 'QY'
                    ? t('scene.chatWorkspace.taskTab.erpOrderForm.label.deliveryLogistics')
                    : t('scene.chatWorkspace.taskTab.erpOrderForm.label.shippingChannel')
                }
                description={
                  erpType === 'QY'
                    ? t('scene.chatWorkspace.taskTab.erpOrderForm.hint.chooseOfflineLogistics')
                    : undefined
                }
                validate={(value) =>
                  value || erpType !== 'QY'
                    ? undefined
                    : t('common.validation.requiredSelection', {
                        fieldName: t(
                          'scene.chatWorkspace.taskTab.erpOrderForm.label.shippingChannel',
                        ),
                      })
                }
              >
                {({ field: { value, onChange, onBlur } }) => {
                  const channels = erpType === 'JST' ? jstChannels : qyTraceChannelState.data;
                  const channelSelect = (
                    <Select
                      allowClear
                      showSearch
                      disabled={erpType === 'JST' ? disabled || !values.channelType : disabled}
                      loading={
                        erpType === 'JST'
                          ? jstTraceChannelState.loading
                          : qyTraceChannelState.loading
                      }
                      dropdownMatchSelectWidth={false}
                      placeholder={t('common.validation.requiredSelection', {
                        fieldName: t(
                          'scene.chatWorkspace.taskTab.erpOrderForm.label.shippingChannel',
                        ),
                      })}
                      value={value?.code}
                      onChange={(value) => {
                        if (!value) {
                          onChange(undefined);
                        } else {
                          const target = channels?.find((_) => _.code === value);
                          if (target) {
                            onChange(target);
                          }
                        }
                      }}
                      onBlur={onBlur}
                      options={channels?.map(({ code, name }) => ({
                        value: code,
                        label: name,
                      }))}
                      optionFilterProp="label"
                    />
                  );
                  if (erpType !== 'JST') {
                    return channelSelect;
                  }
                  const jstChannelSelect = (
                    <Select
                      style={{ marginBottom: 16 }}
                      allowClear
                      showSearch
                      disabled={disabled}
                      loading={jstTraceChannelState.loading}
                      dropdownMatchSelectWidth={false}
                      placeholder={t('common.validation.requiredSelection', {
                        fieldName: t(
                          'scene.chatWorkspace.taskTab.erpOrderForm.label.shippingCarrier',
                        ),
                      })}
                      value={values.channelType}
                      onChange={(value) => {
                        setValues({
                          ...values,
                          channelType: value,
                          traceChannel: undefined,
                        });
                      }}
                      options={jstTraceChannelState.data?.map(({ name, channelType }) => ({
                        value: channelType,
                        label: name,
                      }))}
                      optionFilterProp="label"
                    />
                  );
                  return (
                    <div>
                      {jstChannelSelect}
                      {channelSelect}
                    </div>
                  );
                }}
              </FormField>
              <span />
            </Flex>
            <section
              style={{
                padding: '12px',
                background: 'var(--fett-bg-subtle)',
              }}
            >
              <h4>{t('scene.chatWorkspace.taskTab.erpOrderForm.label.shippingInfo')}</h4>
              {erpType === 'JST' && (
                <div>
                  <Radio
                    disabled={disabled}
                    checked={!newDelivery}
                    onChange={({ target: { checked } }) => checked && setNewDelivery(false)}
                  >
                    {t('scene.chatWorkspace.taskTab.erpOrderForm.action.useRelatedOrderAddress')}
                  </Radio>
                  <Radio
                    disabled={disabled}
                    checked={newDelivery}
                    onChange={({ target: { checked } }) => checked && setNewDelivery(true)}
                  >
                    {t('scene.chatWorkspace.taskTab.erpOrderForm.action.useNewAddress')}
                  </Radio>
                </div>
              )}
              {erpType === 'JST' && newDelivery && <Divider type="horizontal" />}
              {(erpType !== 'JST' || newDelivery) && (
                <>
                  {!disabled && (
                    <div className="mb-4">
                      <Input.TextArea
                        value={text}
                        onChange={({ target: { value } }) => setText(value)}
                        placeholder={t('粘贴文本到此处')}
                      />
                      <StatusButton
                        className="!pl-0"
                        type="link"
                        onClick={() => {
                          const extract = (text: string) => {
                            return extractAddressInfo({ text })
                              .then((res) => {
                                const { name, mobile, zipcode, state, city, district, address } =
                                  res;

                                setValues({
                                  ...values,
                                  receiverName: name,
                                  phone: mobile,
                                  postCode: erpType === 'QY' ? zipcode : values.postCode,
                                  province: state,
                                  city,
                                  district,
                                  address,
                                });
                              })
                              .catch(() => {
                                message.error(t('识别失败，请稍后再试'));
                              });
                          };
                          if (text) {
                            return extract(text);
                          }
                          return navigator.clipboard.readText().then((text) => {
                            if (text) {
                              setText(text);
                              return extract(text);
                            }
                            message.warn(t('未读取到剪切板信息'));
                          });
                        }}
                      >
                        {t(text ? '识别' : '粘贴并识别')}
                      </StatusButton>
                    </div>
                  )}
                  <Flex items={[1, 1, 1]}>
                    <FormField<FV['receiverName']>
                      name="receiverName"
                      required
                      label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.recipientName')}
                      validate={(value) =>
                        value
                          ? undefined
                          : t('common.validation.requiredInput', {
                              fieldName: t(
                                'scene.chatWorkspace.taskTab.erpOrderForm.label.recipientName',
                              ),
                            })
                      }
                    >
                      {({ field }) => {
                        return (
                          <Input
                            showCount
                            disabled={disabled}
                            maxLength={40}
                            placeholder={t(
                              'scene.chatWorkspace.taskTab.erpOrderForm.label.recipientName',
                            )}
                            {...field}
                          />
                        );
                      }}
                    </FormField>
                    <FormField<FV['phone']>
                      name="phone"
                      required
                      label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.phoneNumber')}
                      validate={(value) =>
                        value
                          ? undefined
                          : t('common.validation.requiredInput', {
                              fieldName: t(
                                'scene.chatWorkspace.taskTab.erpOrderForm.label.phoneNumber',
                              ),
                            })
                      }
                    >
                      {({ field }) => {
                        return (
                          <Input
                            disabled={disabled}
                            placeholder={t(
                              'scene.chatWorkspace.taskTab.erpOrderForm.label.phoneNumber',
                            )}
                            {...field}
                          />
                        );
                      }}
                    </FormField>
                    {erpType === 'QY' ? (
                      <FormField<FV['postCode']>
                        name="postCode"
                        required
                        label={t('domain.order.field.postalCode')}
                        validate={(value) =>
                          value
                            ? undefined
                            : t('common.validation.requiredInput', {
                                fieldName: t('domain.order.field.postalCode'),
                              })
                        }
                      >
                        {({ field }) => {
                          return (
                            <Input
                              disabled={disabled}
                              placeholder={t('domain.order.field.postalCode')}
                              {...field}
                            />
                          );
                        }}
                      </FormField>
                    ) : (
                      <div />
                    )}
                  </Flex>
                  <Flex items={[1, 1, 1]}>
                    <FormField<FV['province']>
                      name="province"
                      required
                      label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.addressInfo')}
                      validate={(value) =>
                        value
                          ? undefined
                          : t('common.validation.requiredInput', {
                              fieldName: t(
                                'scene.chatWorkspace.taskTab.erpOrderForm.label.provinceState',
                              ),
                            })
                      }
                    >
                      {({ field }) => {
                        return (
                          <Input
                            disabled={disabled}
                            placeholder={t(
                              'scene.chatWorkspace.taskTab.erpOrderForm.label.provinceState',
                            )}
                            {...field}
                          />
                        );
                      }}
                    </FormField>
                    <FormField<FV['city']>
                      name="city"
                      label=" "
                      validate={(value) =>
                        value
                          ? undefined
                          : t('common.validation.requiredInput', {
                              fieldName: t('scene.chatWorkspace.taskTab.erpOrderForm.label.city'),
                            })
                      }
                    >
                      {({ field }) => {
                        return (
                          <Input
                            disabled={disabled}
                            placeholder={t('scene.chatWorkspace.taskTab.erpOrderForm.label.city')}
                            {...field}
                          />
                        );
                      }}
                    </FormField>
                    <FormField<FV['district']>
                      name="district"
                      label=" "
                      validate={(value) =>
                        value
                          ? undefined
                          : t('common.validation.requiredInput', {
                              fieldName: t(
                                'scene.chatWorkspace.taskTab.erpOrderForm.label.districtCounty',
                              ),
                            })
                      }
                    >
                      {({ field }) => {
                        return (
                          <Input
                            disabled={disabled}
                            placeholder={t(
                              'scene.chatWorkspace.taskTab.erpOrderForm.label.districtCounty',
                            )}
                            {...field}
                          />
                        );
                      }}
                    </FormField>
                  </Flex>
                  <FormField<FV['address']>
                    name="address"
                    required
                    label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.detailedAddress')}
                    validate={(value) =>
                      value
                        ? undefined
                        : t('common.validation.requiredInput', {
                            fieldName: t(
                              'scene.chatWorkspace.taskTab.erpOrderForm.label.detailedAddress',
                            ),
                          })
                    }
                  >
                    {({ field }) => {
                      return (
                        <Input.TextArea
                          disabled={disabled}
                          placeholder={t('common.validation.requiredInput', {
                            fieldName: t(
                              'scene.chatWorkspace.taskTab.erpOrderForm.label.detailedAddress',
                            ),
                          })}
                          {...field}
                        />
                      );
                    }}
                  </FormField>
                </>
              )}
            </section>
          </Form>
        );

        return children(form, {
          ...formProps,
          handleSubmit: (e) => {
            if (skuQuantity && wm && skuQuantity > wm.warehouseInventory) {
              message.error(
                t('scene.chatWorkspace.taskTab.erpOrderForm.feedback.createFailedExceedsStock'),
              );
            } else {
              formProps.handleSubmit(e);
            }
          },
        });
      }}
    </Formik>
  );
}

export type ErpOrderModalProps = ModalProps &
  Pick<ErpOrderFormProps, 'storeId' | 'wms' | 'platform' | 'skuInfo' | 'initialValues'> & {
    mode: 'create' | 'review' | 'view' | 'createReview';
    item: Pick<Conversation.Trade.Product, 'externalItemId' | 'itemPic' | 'itemName'>;
    onUpdate: () => void;
    source?: string; // 用于区分创建补发单的来源
  };

export function ErpOrderModal({
  mode,
  item: itemProp,
  storeId,
  wms,
  platform,
  skuInfo: skuInfoProp,
  initialValues,
  onUpdate,
  source,
  ...otherProps
}: ErpOrderModalProps) {
  const { t } = useTranslate();

  const { visible, show, hide, meta } = useModal(false, {
    item: itemProp as Pick<Conversation.Trade.Product, 'externalItemId' | 'itemPic' | 'itemName'>,
    skuInfo: skuInfoProp,
  });

  const { item = itemProp, skuInfo = skuInfoProp } = meta || {};

  return (
    <ErpOrderForm
      disabled={mode === 'review' || mode === 'view'}
      storeId={storeId}
      wms={wms}
      platform={platform}
      skuInfo={skuInfo}
      initialValues={initialValues}
      onSubmit={(values) => {
        return (
          mode === 'create'
            ? createErpOrder({ erpOrder: { ...values, source } })
            : mode === 'createReview'
            ? createErpOrder({ erpOrder: { ...values, source } }).then(() => {
                return getErpOrder(values.todoId || '')
                  .then((res) => {
                    const targetOrder = find(res.orders, (o) => o.skuId === values.skuId);
                    if (!targetOrder) {
                      return Promise.reject(new Error('no erp order'));
                    }
                    const { todoId, warehouseCode, warehouseName, erpId } = targetOrder;
                    return reviewErpOrder({ todoId, warehouseCode, warehouseName, erpId });
                  })
                  .catch((err) => {
                    onUpdate();
                    return Promise.reject(err);
                  });
              })
            : mode === 'review'
            ? reviewErpOrder({
                erpId: values?.erpId || '',
                todoId: values.todoId || '',
                warehouseName: values.warehouseName || '',
                warehouseCode: values.warehouseCode || '',
              })
            : Promise.reject(new Error('invalid mode'))
        )
          .then(() => {
            message.success(t('scene.chatWorkspace.taskTab.erpOrderForm.feedback.saveSuccess'));
            onUpdate();
          })
          .catch((e) => {
            message.error(
              e?.response?.data?.msg ||
                t('scene.chatWorkspace.taskTab.erpOrderForm.feedback.saveFailed'),
            );
          });
      }}
    >
      {(form, { values: { erpTradeId }, setValues, isSubmitting, handleSubmit }) => {
        return (
          <Modal
            width={680}
            title={
              {
                create: t('scene.chatWorkspace.taskTab.erpOrder.action.createReshipment'),
                review: t('scene.chatWorkspace.taskTab.erpOrder.action.auditReshipment'),
                view: t('scene.chatWorkspace.taskTab.erpOrder.action.viewReshipment'),
                createReview: t('scene.chatWorkspace.taskTab.erpOrder.action.createReshipment'),
              }[mode]
            }
            footer={
              mode === 'view' ? (
                false
              ) : (
                <div className="flex items-center justify-end">
                  {mode === 'review' && (
                    <div className="mr-4">
                      {t('scene.chatWorkspace.taskTab.erpOrder.label.reshipmentOrderNo')}：
                      {erpTradeId}
                      <CopyOutlined
                        onClick={() => {
                          copyText(erpTradeId || '').then(() => {
                            message.success(t('common.feedback.copySuccess'));
                          });
                        }}
                      />
                    </div>
                  )}
                  <Button onClick={otherProps.onCancel}>{t('common.action.cancel')}</Button>
                  <Button type="primary" loading={isSubmitting} onClick={() => handleSubmit()}>
                    {mode === 'create' && t('common.action.create')}
                    {mode === 'review' &&
                      t('scene.chatWorkspace.taskTab.erpOrderForm.action.confirmAudit')}
                    {mode === 'createReview' &&
                      t('scene.chatWorkspace.taskTab.erpOrderForm.action.createAndAudit')}
                  </Button>
                </div>
              )
            }
            {...otherProps}
          >
            <Item>
              <SafeImage hasSmallPic="both" src={item.itemPic} width={48} height={48} />
              <section>{item.itemName}</section>
              {(mode === 'create' || mode === 'createReview') && [
                <Divider type="vertical" />,
                <Button type="text" icon={<FormOutlined />} onClick={() => show(meta)}>
                  {t('common.action.modify')}
                </Button>,
              ]}
              {meta && (
                <ItemSelectModal
                  visible={visible}
                  storeId={storeId}
                  {...meta}
                  onCancel={() => hide(meta)}
                  onChange={(item, skuInfo) => {
                    hide({ item, skuInfo });
                    setValues((values) => ({
                      ...values,
                      itemId: item.externalItemId,
                      skuId: '',
                      skuName: '',
                    }));
                  }}
                />
              )}
            </Item>
            {form}
          </Modal>
        );
      }}
    </ErpOrderForm>
  );
}

function ItemSelectModal({
  visible,
  storeId,
  item,
  skuInfo,
  onChange,
  onCancel,
  ...otherProps
}: {
  storeId: number;
  item: Pick<Conversation.Trade.Product, 'externalItemId' | 'itemPic' | 'itemName'>;
  skuInfo: Conversation.ProductSKUInfo[];
  onChange: (
    item: Pick<Conversation.Trade.Product, 'externalItemId' | 'itemPic' | 'itemName'>,
    skuInfo: Conversation.ProductSKUInfo[],
  ) => void;
} & ModalProps) {
  const { t } = useTranslate();

  const [activeItem, setActiveItem] = useState<Conversation.Product>();
  useEffect(() => {
    setActiveItem(undefined);
  }, [item.externalItemId]);

  const [keyword, setKeyword] = useState('');
  const [keywordType, setKeywordType] = useState<0 | 1 | 2 | 3>(0);
  const [_keywordType, set_keywordType] = useState<0 | 1 | 2 | 3>(0);

  const {
    loading,
    data: { itemList, pageInfo } = {},
    runAsync,
  } = useRequest(
    ({ page, pageSize } = { page: 1, pageSize: 10 }) => {
      return productList({
        internal_store_id: storeId,
        keyword,
        keyword_type: _keywordType,
        page_no: page,
        page_size: pageSize,
        needStockRule: true,
        warehouseType: 'REISSUE_WAREHOUSE',
      });
    },
    {
      refreshDeps: [storeId, keyword, _keywordType],
      ready: visible,
      onError: () => message.error(t('common.feedback.fetchFailed')),
    },
  );

  return (
    <StickyModal
      visible={visible}
      title={t('common.action.modify', {
        x: t('scene.chatWorkspace.taskTab.erpOrderForm.label.product'),
      })}
      width={602}
      onCancel={onCancel}
      onOk={(e) => {
        onCancel?.(e);
        if (activeItem && activeItem.externalItemId !== item.externalItemId) {
          onChange(
            {
              externalItemId: activeItem.externalItemId,
              itemName: activeItem.itemName,
              itemPic: activeItem.imageUrl[0],
            },
            activeItem.skuInfo,
          );
        }
      }}
      {...otherProps}
    >
      <ItemSelectContent>
        <header>
          <Input.Search
            allowClear
            placeholder={t('common.placeholder.input')}
            onSearch={(value) => {
              setKeyword(value);
              set_keywordType(keywordType);
            }}
            addonBefore={
              <Select
                dropdownMatchSelectWidth={false}
                value={keywordType}
                onChange={setKeywordType}
                options={[
                  { value: 0, label: t('domain.product.field.name') },
                  { value: 1, label: t('domain.product.field.id') },
                  { value: 2, label: 'SKU' },
                  { value: 3, label: t('domain.product.field.skuCode') },
                ]}
              />
            }
          />
        </header>
        {itemList?.length ? (
          <>
            <ul>
              {itemList.map((cur) => {
                const {
                  internalItemId,
                  externalItemId,
                  imageUrl: [itemPic],
                  itemName,
                  itemSku,
                } = cur;
                const checked = activeItem
                  ? activeItem.internalItemId === internalItemId
                  : item.externalItemId === externalItemId;
                return (
                  <li onClick={() => setActiveItem(cur)}>
                    <Radio checked={checked} />
                    <SafeImage src={itemPic} width={64} height={64} />
                    <section>
                      <div>{itemName}</div>
                      <p>
                        ID：{externalItemId}
                        <Divider type="vertical" />
                        {t('domain.product.field.primarySkuCode')}：{itemSku || '-'}
                      </p>
                    </section>
                  </li>
                );
              })}
            </ul>
            <footer>
              <Pagination
                size="small"
                showQuickJumper
                showSizeChanger
                current={pageInfo?.pageNo}
                pageSize={pageInfo?.pageSize}
                total={pageInfo?.totalCount}
                onChange={(page, pageSize) => runAsync({ page, pageSize })}
              />
            </footer>
          </>
        ) : loading ? (
          <LoadingOutlined />
        ) : (
          <Empty />
        )}
      </ItemSelectContent>
    </StickyModal>
  );
}

const Item = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  margin-bottom: 16px;
  border: solid 1px var(--fett-border-strong);
  > div {
    flex: 0 0 auto;
    margin-right: 12px;
  }
  > section {
    flex: 1 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
`;

const Flex = styled.div<{
  items: number[];
}>`
  display: flex;
  align-items: center;
  gap: 16px;
  > * {
    overflow: hidden;
  }
  > *:first-of-type {
    flex: ${({ items }) => `${items[0]} 0 0`};
  }
  > *:nth-child(2) {
    flex: ${({ items }) => `${items[1]} 0 0`};
  }
  > *:nth-child(3) {
    flex: ${({ items }) => `${items[2]} 0 0`};
  }
`;

const ItemSelectContent = styled.div`
  margin: -24px;
  > header {
    padding: 10px 16px;
    border-bottom: solid 1px var(--fett-border-default);
    > span {
      width: 300px;
    }
  }
  > ul {
    margin: 0;
    padding: 0;
    list-style: none;
    > li {
      display: flex;
      align-items: center;
      margin: 16px;
      padding: 10px;
      border: solid 1px var(--fett-border-strong);
      cursor: pointer;
      > div {
        margin-right: 8px;
      }
      > section {
        flex: 1 0 0;
        align-self: stretch;
        display: flex;
        flex-direction: column;
        justify-content: space-around;
        overflow: hidden;
        > div {
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        > p {
          margin-bottom: 0;
          font-size: 12px;
          color: var(--fett-text-tertiary);
        }
      }
    }
  }
  > footer {
    margin: 16px;
    > ul {
      text-align: right;
    }
  }
`;

export default ErpOrderForm;
